import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useFeatureFlags } from '../../context/FeatureFlagsContext';
import DisabledFeatureBanner from '../../components/FeatureGate/DisabledFeatureBanner';
import {
    Box,
    Card,
    CardContent,
    Typography,
    CircularProgress,
    Button,
} from '@mui/material';
import {
    CheckCircle,
    Error as ErrorIcon,
    Home,
} from '@mui/icons-material';

const PaymentComplete = ({ snackBar }) => {
    const { isEnabled, getMessage } = useFeatureFlags();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const [status, setStatus] = useState('loading'); // 'loading', 'success', 'error'

    const reference = searchParams.get('reference') || searchParams.get('trxref');

    useEffect(() => {
        if (reference) {
            // Payment was successful (Paystack redirects here after successful payment)
            setStatus('success');

            // Show success notification
            if (snackBar) {
                snackBar({
                    text: 'Payment successful! Your account has been credited.',
                    type: 'success',
                });
            }

            // Auto-redirect after 5 seconds
            const timer = setTimeout(() => {
                navigate('/home');
            }, 5000);

            return () => clearTimeout(timer);
        } else {
            setStatus('error');
        }
    }, [reference, navigate, snackBar]);

    const handleGoToDashboard = () => {
        navigate('/home');
    };

    if (!isEnabled('payments')) {
        return <DisabledFeatureBanner featureName="Payments" message={getMessage('payments')} />;
    }

    return (
        <Box
            sx={{
                minHeight: '100vh',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: '#f5f5f5',
                padding: 2,
            }}
        >
            <Card
                sx={{
                    maxWidth: 450,
                    width: '100%',
                    borderRadius: '16px',
                    boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
                    textAlign: 'center',
                }}
            >
                <CardContent sx={{ padding: '48px 32px' }}>
                    {status === 'loading' && (
                        <>
                            <CircularProgress
                                size={64}
                                sx={{ color: '#3A5A78', marginBottom: 3 }}
                            />
                            <Typography variant="h5" sx={{ color: '#3A5A78', fontWeight: 600 }}>
                                Processing Payment...
                            </Typography>
                            <Typography variant="body2" sx={{ color: '#6B7280', marginTop: 1 }}>
                                Please wait while we confirm your payment.
                            </Typography>
                        </>
                    )}

                    {status === 'success' && (
                        <>
                            <Box
                                sx={{
                                    width: 80,
                                    height: 80,
                                    borderRadius: '50%',
                                    backgroundColor: '#D1FAE5',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    margin: '0 auto 24px',
                                }}
                            >
                                <CheckCircle sx={{ fontSize: 48, color: '#10B981' }} />
                            </Box>

                            <Typography
                                variant="h5"
                                sx={{ color: '#10B981', fontWeight: 600, marginBottom: 1 }}
                            >
                                Payment Successful!
                            </Typography>

                            <Typography variant="body1" sx={{ color: '#4B5563', marginBottom: 1 }}>
                                Your payment has been processed successfully.
                            </Typography>

                            {reference && (
                                <Typography
                                    variant="body2"
                                    sx={{
                                        color: '#6B7280',
                                        marginBottom: 3,
                                        padding: '8px 16px',
                                        backgroundColor: '#F3F4F6',
                                        borderRadius: '8px',
                                        display: 'inline-block',
                                    }}
                                >
                                    Reference: <strong>{reference}</strong>
                                </Typography>
                            )}

                            <Typography
                                variant="body2"
                                sx={{ color: '#9CA3AF', marginBottom: 3 }}
                            >
                                Your account balance will be updated shortly.
                                <br />
                                Redirecting to dashboard in 5 seconds...
                            </Typography>

                            <Button
                                variant="contained"
                                startIcon={<Home />}
                                onClick={handleGoToDashboard}
                                sx={{
                                    backgroundColor: '#3A5A78',
                                    '&:hover': { backgroundColor: '#2d4760' },
                                    textTransform: 'none',
                                    padding: '10px 24px',
                                    borderRadius: '8px',
                                }}
                            >
                                Go to Dashboard
                            </Button>
                        </>
                    )}

                    {status === 'error' && (
                        <>
                            <Box
                                sx={{
                                    width: 80,
                                    height: 80,
                                    borderRadius: '50%',
                                    backgroundColor: '#FEE2E2',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    margin: '0 auto 24px',
                                }}
                            >
                                <ErrorIcon sx={{ fontSize: 48, color: '#EF4444' }} />
                            </Box>

                            <Typography
                                variant="h5"
                                sx={{ color: '#EF4444', fontWeight: 600, marginBottom: 1 }}
                            >
                                Payment Issue
                            </Typography>

                            <Typography variant="body1" sx={{ color: '#4B5563', marginBottom: 3 }}>
                                We couldn't verify your payment. If you completed the payment,
                                please check your dashboard - it may take a few moments to reflect.
                            </Typography>

                            <Button
                                variant="contained"
                                startIcon={<Home />}
                                onClick={handleGoToDashboard}
                                sx={{
                                    backgroundColor: '#3A5A78',
                                    '&:hover': { backgroundColor: '#2d4760' },
                                    textTransform: 'none',
                                    padding: '10px 24px',
                                    borderRadius: '8px',
                                }}
                            >
                                Go to Dashboard
                            </Button>
                        </>
                    )}
                </CardContent>
            </Card>
        </Box>
    );
};

export default PaymentComplete;
